/* global Promise */
import $ from "jquery";
import BD_Component from "@bd-core/bd-component";
import compile from "@bd-utils/handlerbars-helper";
import bdapi from "@bd-core/bd-api";
import bdjs from "@bd-core/bdjs";
import GameLogic from "@bd-core/game-logic";

/** Max avatar file size in bytes (5MB), must match backend AVATAR_MAX_BYTES. */
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/** Max dimension (width or height) when compressing to avoid huge canvases. */
const AVATAR_MAX_DIMENSION = 2048;

/**
 * Compress an image File to stay under maxBytes. Uses Canvas; outputs JPEG.
 * @param {File} file Image file.
 * @param {number} maxBytes Maximum size in bytes.
 * @returns {Promise<File>} Compressed file (JPEG).
 */
function compressAvatarToMaxSize(file, maxBytes) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            let width = w;
            let height = h;
            if (width > AVATAR_MAX_DIMENSION || height > AVATAR_MAX_DIMENSION) {
                if (width >= height) {
                    height = Math.round((height * AVATAR_MAX_DIMENSION) / width);
                    width = AVATAR_MAX_DIMENSION;
                } else {
                    width = Math.round((width * AVATAR_MAX_DIMENSION) / height);
                    height = AVATAR_MAX_DIMENSION;
                }
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new Error("Canvas not supported"));
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);

            const tryQuality = (quality) => {
                return new Promise((res) => {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                res({ blob: null, quality });
                                return;
                            }
                            res({ blob, quality });
                        },
                        "image/jpeg",
                        quality,
                    );
                });
            };

            const attempt = (quality, w, h) => {
                canvas.width = w;
                canvas.height = h;
                ctx.drawImage(img, 0, 0, w, h);
                tryQuality(quality).then(({ blob }) => {
                    if (!blob || blob.size <= maxBytes) {
                        const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
                        resolve(new File([blob || new Blob()], name, { type: "image/jpeg" }));
                        return;
                    }
                    const next = Math.max(0.1, quality - 0.15);
                    if (next <= 0.1) {
                        const minDim = 100;
                        const newW = Math.max(minDim, Math.round(w * 0.75));
                        const newH = Math.max(minDim, Math.round(h * 0.75));
                        if (newW < w || newH < h) {
                            attempt(0.9, newW, newH);
                            return;
                        }
                        const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
                        resolve(new File([blob], name, { type: "image/jpeg" }));
                        return;
                    }
                    attempt(next, w, h);
                });
            };
            attempt(0.9, width, height);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Failed to load image"));
        };
        img.src = url;
    });
}

export class LoginPage extends BD_Component {
    constructor() {
        super();

        this.id = "login-page";
        this.template = "login-page-template";

        this.elms = {
            loginPage: ".login-page",
            app: ".app",
            modal: ".login-page__modal",
            modalBackButton: ".login-page__modal__back",
            modalTitle: ".login-page__modal__title",
            modalInput: ".login-page__modal__input",
            modalContent: ".modal__content",
            selectPlayerModal: ".select-player",
            createAccountModal: ".create-account",
            pincodeModal: ".pincode",
            enterCodeModal: ".enter-code",
            uploadImage: ".upload-image",
            uploadImageInput: ".upload-image__input",
            uploadImageInputId: "upload-image-input",
            uploadImagePreview: ".upload-image__preview",
            playerGrid: ".login-page__player-grid",
            submitButton: ".login-page__modal__submit",
            submitButtonLabel: ".login-page__modal__submit-label",
            numberGridItem: ".number-grid__item",
            playerItem: ".player",
            storeItem: ".store",
            loginPageInput: ".login-page__input",
            loginPageButton: ".login-page__button",
            pincodeErrorModal: ".pincode-confirm-error",
            pincodeErrorRetry: ".modal__actions__retry",
        };

        this.classes = {
            modalOpen: "modal--is-open",
            uploadHasPreview: "upload-image--has-preview",
            createAccount: "create-account",
            pincode: "pincode",
            confirm: "confirm",
            backspace: "backspace",
            clicked: "clicked",
            enterCode: "enter-code",
            invalid: "invalid",
            isLoading: "is-loading",
        };

        /** @type {string|null} */
        this._uploadImageObjectUrl = null;
        /** @type {File|null} */
        this._createAccountFile = null;
        /** @type {string|null} */
        this._createAccountPincode = null;
        /** @type {number} When completing an existing user's profile (has_profile false), the user ID to update. */
        this._createAccountEditingUserId = 0;
        this._isSubmittingCreateAccount = false;

        this._events = this.getEvents();

        this.pincodeLength = 4;
        this.teamId = 0;
        this.teamMembers = [];
        this.userId = 0;
    }

    getEvents() {
        const elms = this.elms;
        return [
            {
                selector: elms.modalBackButton,
                event: "click",
                handler: this.onModalBackButtonClick,
            },
            {
                selector: elms.loginPageButton,
                event: "click",
                handler: this.handleStoreCodeSubmit,
            },
            {
                selector: elms.loginPageInput,
                event: "keydown",
                handler: this.onLoginPageInputKeydown,
            },
            {
                selector: elms.loginPageInput,
                event: "input",
                handler: this.onLoginPageInput,
            },
            {
                selector: elms.uploadImage,
                event: "click",
                handler: this.handleUploadImageClick,
            },
            {
                selector: elms.uploadImageInput,
                event: "change",
                handler: this.handleImageUpload,
            },
            {
                selector: elms.submitButton,
                event: "click",
                handler: this.handleSubmit,
            },
            {
                selector: elms.numberGridItem,
                event: "pointerdown",
                handler: this.handleNumberGridItem,
            },
            {
                selector: elms.numberGridItem,
                event: "pointerup",
                handler: this.onNumberGridItemRelease,
            },
            {
                selector: elms.numberGridItem,
                event: "pointerleave",
                handler: this.clearNumberGridItemClicked,
            },
            {
                selector: document,
                event: "pointerup",
                handler: this.clearNumberGridClicked,
            },
            {
                selector: document,
                event: "pointercancel",
                handler: this.clearNumberGridClicked,
            },
            {
                selector: elms.playerItem,
                event: "click",
                handler: this.handlePlayerItem,
            },
            {
                selector: elms.storeItem,
                event: "click",
                handler: this.handleStoreItem,
            },
            {
                selector: elms.enterCodeModal,
                event: "click",
                handler: this.onCloseCodeModal,
            },
            {
                selector: this.elms.createAccountModal + " [data-field], " + this.elms.createAccountModal + " .upload-image",
                event: "input change",
                handler: this.updateCreateAccountSubmitState,
            },
            {
                selector: elms.pincodeErrorRetry,
                event: "click",
                handler: this.handlePincodeErrorRetry,
            },
        ];
    }

    beforeInit() {
        this.render();
        this.setGameState();
    }

    render() {
        const data = this._data;

        const htmlTemplate = $("#" + this.template).html();
        const html = compile(htmlTemplate, data);
        $(this.elms.app).html(html);
    }

    /**
     * Restrict store code input to digits only, max 4 characters.
     */
    onLoginPageInput() {
        const $input = $(this.elms.loginPageInput);
        const val = String($input.val() ?? "")
            .replace(/\D/g, "")
            .slice(0, 4);
        if ($input.val() !== val) $input.val(val);
        this.updateLoginButtonState();
    }

    /**
     * Enable login button only when store code input is exactly 4 digits.
     */
    updateLoginButtonState() {
        const val = String($(this.elms.loginPageInput).val() ?? "").trim();
        const isValid = /^\d{4}$/.test(val);
        $(this.elms.loginPageButton).prop("disabled", !isValid);
    }

    setGameState() {
        this.updateLoginButtonState();
        const storedTeamId = localStorage.getItem("teamId");
        if (storedTeamId) {
            const id = Number(storedTeamId);
            if (!Number.isNaN(id)) {
                this.teamId = id;
                this.getTeamPlayers();
                $(this.elms.selectPlayerModal).addClass(this.classes.modalOpen);
            }
        }
    }

    beforeDestroy() {
        if (this._uploadImageObjectUrl) {
            URL.revokeObjectURL(this._uploadImageObjectUrl);
        }
    }

    // ===== HANDLERS =====
    onModalBackButtonClick(e) {
        e.preventDefault();
        const modal = $(e.currentTarget).closest(this.elms.modal);
        const backWrapper = $(e.currentTarget).closest(
            ".login-page__modal__back",
        );
        if (backWrapper.data("back-to-store")) {
            localStorage.removeItem("teamId");
            this.teamId = 0;
            $(this.elms.loginPageInput).val("").removeClass(this.classes.invalid);
            this.updateLoginButtonState();
        }
        if(backWrapper.data("back-to-set-pincode")) {
            modal.removeClass(this.classes.confirm);
            this.clearPincodeSubmitLoading();
            modal.find(this.elms.modalTitle).text(this._data.login_page.set_pin.instruction_text);
            modal.find(this.elms.modalInput).val("").removeClass(this.classes.invalid);
            modal.find(this.elms.modalBackButton).data("back-to-set-pincode", false);
            this.setPincodeSubmitLabelText(
                modal.find(this.elms.submitButton),
                this._data.login_page.set_pin.primary_button_label_set,
            );
            modal.find(this.elms.modalBackButton).find("span").text(this._data.login_page.set_pin.secondary_button_label_set);
            return;
        }
        if (modal.hasClass(this.classes.createAccount)) {
            this._createAccountFile = null;
            this._createAccountPincode = null;
            this._createAccountEditingUserId = 0;
            if (this._uploadImageObjectUrl) {
                URL.revokeObjectURL(this._uploadImageObjectUrl);
                this._uploadImageObjectUrl = null;
            }
            const $createModal = $(this.elms.createAccountModal);
            $createModal.find(this.elms.uploadImage).removeClass(this.classes.uploadHasPreview);
            $createModal.find(this.elms.uploadImagePreview).empty();
            const input = document.getElementById(this.elms.uploadImageInputId);
            if (input && "value" in input) input.value = "";
        }
        if (modal.hasClass(this.classes.pincode)) {
            modal.removeClass(this.classes.confirm);
            this.clearPincodeSubmitLoading();
            this.setPincodeSubmitLabelText(
                modal.find(this.elms.submitButton),
                this._data.login_page.set_pin.primary_button_label_set,
            );
            modal.find(this.elms.modalTitle).text(this._data.login_page.set_pin.instruction_text);
            modal.find(this.elms.modalInput).val("").removeClass(this.classes.invalid);
        }
        modal.removeClass(this.classes.modalOpen);
    }

    async handleStoreCodeSubmit(e) {
        e.preventDefault();

        const $input = $(this.elms.loginPageInput);
        this.storePincodeAction($input);
    }

    onLoginPageInputKeydown() {
        $(this.elms.loginPageInput).removeClass(this.classes.invalid);
    }

    handleUploadImageClick(e) {
        e.preventDefault();
        const input = document.getElementById(this.elms.uploadImageInputId);
        if (input) input.click();
    }

    handleImageUpload(e, $el) {
        const self = this;

        const input = e.target;
        const file = input.files && input.files[0];
        if (!file || !file.type.startsWith("image/")) return;

        this._createAccountFile = file;

        const button = $el.next(this.elms.uploadImage);
        const previewEl = button.find(this.elms.uploadImagePreview);

        if (self._uploadImageObjectUrl) {
            URL.revokeObjectURL(self._uploadImageObjectUrl);
        }
        self._uploadImageObjectUrl = URL.createObjectURL(file);

        previewEl.empty().append(
            $("<img />", {
                src: self._uploadImageObjectUrl,
                alt: "Chosen picture",
            }),
        );
        button.addClass(this.classes.uploadHasPreview);

        input.value = "";
        this.updateCreateAccountSubmitState();
    }

    resetCreateAccountForm() {
        const $modal = $(this.elms.createAccountModal);
        $modal.find("[data-field]").val("").removeClass(this.classes.invalid);
        $modal.find(this.elms.uploadImage).removeClass(this.classes.invalid).removeClass(this.classes.uploadHasPreview);
        $modal.find(this.elms.uploadImagePreview).empty();
        const input = document.getElementById(this.elms.uploadImageInputId);
        if (input && "value" in input) input.value = "";
        if (this._uploadImageObjectUrl) {
            URL.revokeObjectURL(this._uploadImageObjectUrl);
            this._uploadImageObjectUrl = null;
        }
        this._createAccountFile = null;
        this._createAccountEditingUserId = 0;
    }

    openCreatePlayerModal(e, prefill) {
        e.preventDefault();
        const $modal = $(this.elms.createAccountModal);

        if (prefill && (prefill.first_name != null || prefill.last_name != null)) {
            $modal.find('[data-field="first_name"]').val(prefill.first_name ?? "");
            $modal.find('[data-field="last_name"]').val(prefill.last_name ?? "");
        } else {
            this.resetCreateAccountForm();
        }

        $(this.elms.createAccountModal).addClass(this.classes.modalOpen);
        this.updateCreateAccountSubmitState();
    }

    /**
     * Enable/disable create account submit button based on form completeness.
     */
    updateCreateAccountSubmitState() {
        const $modal = $(this.elms.createAccountModal);
        if (!$modal.hasClass(this.classes.modalOpen)) return;
        const values = this.getCreateAccountFormValues();
        const hasImage = !!(this._createAccountFile && this._createAccountFile.type.startsWith("image/"));
        const complete =
            hasImage &&
            values.first_name.length > 0 &&
            values.last_name.length > 0 &&
            values.question.length > 0;
        $modal.find(this.elms.submitButton).prop("disabled", !complete);
    }

    getCreateAccountFormValues() {
        const $modal = $(this.elms.createAccountModal);
        return {
            first_name: String(
                $modal.find('[data-field="first_name"]').val() ?? "",
            ).trim(),
            last_name: String(
                $modal.find('[data-field="last_name"]').val() ?? "",
            ).trim(),
            question: String(
                $modal.find('[data-field="question"]').val() ?? "",
            ).trim(),
        };
    }

    validateCreateAccountPhase1() {
        const $modal = $(this.elms.createAccountModal);
        const $upload = $modal.find(this.elms.uploadImage);
        const values = this.getCreateAccountFormValues();

        $modal.find("[data-field]").removeClass(this.classes.invalid);
        $upload.removeClass(this.classes.invalid);

        let valid = true;

        if (!this._createAccountFile || !this._createAccountFile.type.startsWith("image/")) {
            $upload.addClass(this.classes.invalid);
            valid = false;
        }

        if (!values.first_name) {
            $modal.find('[data-field="first_name"]').addClass(this.classes.invalid);
            valid = false;
        }
        if (!values.last_name) {
            $modal.find('[data-field="last_name"]').addClass(this.classes.invalid);
            valid = false;
        }
        if (!values.question) {
            $modal.find('[data-field="question"]').addClass(this.classes.invalid);
            valid = false;
        }

        return valid;
    }

    getPincodeSubmitButton() {
        return $(this.elms.pincodeModal).find(this.elms.submitButton);
    }

    getPincodeSubmitLabel($btn) {
        const $button = $btn || this.getPincodeSubmitButton();
        const $label = $button.find(this.elms.submitButtonLabel);
        return $label.length ? $label : $button;
    }

    setPincodeSubmitLabelText($btn, text) {
        this.getPincodeSubmitLabel($btn).text(text);
    }

    setPincodeSubmitLoading(loading) {
        const $btn = this.getPincodeSubmitButton();
        this._isSubmittingCreateAccount = loading;
        $btn.prop("disabled", loading).toggleClass(this.classes.isLoading, loading);
        if (!loading) {
            this.updatePincodeSubmitState();
        }
    }

    clearPincodeSubmitLoading() {
        this.setPincodeSubmitLoading(false);
    }

    handleSubmit(e) {
        e.preventDefault();

        const modal = $(e.currentTarget).closest(this.elms.modal);
        if (modal.hasClass(this.classes.createAccount)) {
            if (!this.validateCreateAccountPhase1()) return;
            $(this.elms.pincodeModal).addClass(this.classes.modalOpen);
            this.updatePincodeSubmitState();
        } else if (modal.hasClass(this.classes.pincode)) {
            const pincodeInput = modal.find(this.elms.modalInput);
            const pincodeVal = String(pincodeInput.val() ?? "");

            if (modal.hasClass(this.classes.confirm)) {
                if (this._isSubmittingCreateAccount) return;
                if (pincodeVal.length !== this.pincodeLength) return;
                if (pincodeVal !== this._createAccountPincode) {
                    pincodeInput.val("").addClass(this.classes.invalid);
                    console.error("Pincode does not match");
                    $(this.elms.pincodeErrorModal).addClass(this.classes.modalOpen);
                    this.updatePincodeSubmitState();
                    return;
                }
                pincodeInput.removeClass(this.classes.invalid);
                this.submitCreateAccount();
            } else {
                if (pincodeVal.length !== this.pincodeLength) return;
                this._createAccountPincode = pincodeVal;
                modal.addClass(this.classes.confirm);
                modal.find(this.elms.modalTitle).text(this._data.login_page.set_pin.confirm_instruction_text);
                modal.find(this.elms.modalBackButton).find("span").text(this._data.login_page.set_pin.secondary_button_label_confirm);
                modal.find(this.elms.modalBackButton).data("back-to-set-pincode", true);
                this.setPincodeSubmitLabelText(
                    modal.find(this.elms.submitButton),
                    this._data.login_page.set_pin.primary_button_label_confirm,
                );
                pincodeInput.val("").removeClass(this.classes.invalid);
                this.updatePincodeSubmitState();
            }
        }
    }

    handlePincodeErrorRetry(e) {
        e.preventDefault();
        const modal = $(this.elms.pincodeErrorModal);
        modal.removeClass(this.classes.modalOpen);
    }

    /**
     * Enable/disable set/confirm PIN submit button based on 4-digit input.
     */
    updatePincodeSubmitState() {
        const $modal = $(this.elms.pincodeModal);
        if (!$modal.length) return;
        if (this._isSubmittingCreateAccount) return;
        const len = String($modal.find(this.elms.modalInput).val() ?? "").length;
        $modal.find(this.elms.submitButton).prop("disabled", len !== this.pincodeLength);
    }

    async submitCreateAccount() {
        if (this._isSubmittingCreateAccount) return;

        let avatarFile = this._createAccountFile;
        const pincodeModal = $(this.elms.pincodeModal);
        const pincodeInput = pincodeModal.find(this.elms.modalInput);
        const $submitBtn = this.getPincodeSubmitButton();

        this.setPincodeSubmitLoading(true);

        let failed = false;
        try {
            if (avatarFile.size > AVATAR_MAX_BYTES) {
                try {
                    avatarFile = await compressAvatarToMaxSize(avatarFile, AVATAR_MAX_BYTES);
                } catch (err) {
                    console.error(err);
                    pincodeInput.addClass(this.classes.invalid);
                    failed = true;
                    return;
                }
            }

            const formValues = this.getCreateAccountFormValues();
            const formData = new FormData();
            formData.append("avatar", avatarFile);
            formData.append("first_name", formValues.first_name);
            formData.append("last_name", formValues.last_name);
            formData.append("question", formValues.question);
            formData.append("pincode", this._createAccountPincode);
            formData.append("confirm_pincode", this._createAccountPincode);
            formData.append("team_id", String(this.teamId));
            if (this._createAccountEditingUserId > 0) {
                formData.append("user_id", String(this._createAccountEditingUserId));
            }

            const [res, createErr] = await bdjs.goAsync(
                bdapi.post("public/create-account", formData),
            );
            if (!res || !res.data) {
                throw createErr || new Error("Failed to create account");
            }
            const response = res.data;
            if (response.success) {
                this._isSubmittingCreateAccount = false;
                this._createAccountPincode = null;
                this.resetCreateAccountForm();
                $(this.elms.createAccountModal).removeClass(this.classes.modalOpen);
                pincodeModal.removeClass(this.classes.modalOpen);
                pincodeModal.removeClass(this.classes.confirm);
                pincodeModal.find(this.elms.modalTitle).text(this._data.login_page.set_pin.instruction_text);
                pincodeModal.find(this.elms.modalBackButton).find("span").text(this._data.login_page.set_pin.secondary_button_label_set);
                $submitBtn.prop("disabled", true).removeClass(this.classes.isLoading);
                this.setPincodeSubmitLabelText(
                    $submitBtn,
                    this._data.login_page.set_pin.primary_button_label_set,
                );
                pincodeInput.val("");
                // Navigate to account list first so user always gets back after successful create
                $(this.elms.selectPlayerModal).addClass(this.classes.modalOpen);
                // Short delay before follow-up request to avoid Safari/iPadOS connection reuse issues
                await new Promise((r) => setTimeout(r, 150));
                let refreshed = await this.getTeamPlayers();
                if (!refreshed) {
                    await new Promise((r) => setTimeout(r, 300));
                    refreshed = await this.getTeamPlayers();
                }
                if (!refreshed) {
                    console.warn("Could not refresh team list; account was created. User can refresh the list.");
                }
            } else {
                pincodeInput.addClass(this.classes.invalid);
                failed = true;
            }
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to create account";
            pincodeInput.addClass(this.classes.invalid);
            console.error(message);
            failed = true;
        } finally {
            if (failed) {
                this.clearPincodeSubmitLoading();
            }
        }
    }

    async handleNumberGridItem(e) {
        e.preventDefault();
        const modal = $(e.currentTarget).closest(this.elms.modal);
        const input = modal.find(this.elms.modalInput);

        if (
            String(input.val() ?? "").length === this.pincodeLength &&
            !$(e.currentTarget).hasClass(this.classes.backspace)
        ) {
            return;
        }
        if (
            String(input.val() ?? "").length === 0 &&
            $(e.currentTarget).hasClass(this.classes.backspace)
        ) {
            return;
        }

        $(e.currentTarget).addClass(this.classes.clicked);

        if ($(e.currentTarget).hasClass(this.classes.backspace)) {
            input.val(String(input.val() ?? "").slice(0, -1));
        } else {
            input.val(String(input.val() ?? "") + $(e.currentTarget).text());
        }

        if (modal.hasClass(this.classes.pincode)) {
            this.updatePincodeSubmitState();
        }

        // temporary invalid state check
        if (modal.hasClass(this.classes.enterCode)) {
            if (String(input.val() ?? "").length === this.pincodeLength) {
                if($(this.elms.selectPlayerModal).hasClass(this.classes.modalOpen)) {
                    this.playerPincodeAction(input);
                } else {
                    this.storePincodeAction(input);
                }
            } else {
                input.removeClass(this.classes.invalid);
            }
        }
    }

    onNumberGridItemRelease(e) {
        e.preventDefault();
        $(e.currentTarget).removeClass(this.classes.clicked);
    }

    clearNumberGridItemClicked(e) {
        $(e.currentTarget).removeClass(this.classes.clicked);
    }

    clearNumberGridClicked() {
        $(this.elms.numberGridItem).removeClass(this.classes.clicked);
    }

    handlePlayerItem(e) {
        e.preventDefault();
        const $player = $(e.currentTarget);
        const hasProfile = $player.data("has-profile");

        if (hasProfile === false) {
            const firstName = $player.data("first-name") ?? "";
            const lastName = $player.data("last-name") ?? "";
            this._createAccountEditingUserId = $player.data("player-id") ?? 0;
            this.openCreatePlayerModal(e, { first_name: firstName, last_name: lastName });
            return;
        }

        this.userId = $player.data("player-id");
        $(this.elms.enterCodeModal).addClass(this.classes.modalOpen);
    }

    handleStoreItem(e) {
        e.preventDefault();
        this.teamId = $(e.currentTarget).data("team-id");
        $(this.elms.enterCodeModal).addClass(this.classes.modalOpen);
    }

    onCloseCodeModal(e) {
        if (e) {
            e.preventDefault();
        }

        if (!e || !$(e.target).closest(this.elms.modalContent).length) {
            const enterCodeModal = $(this.elms.enterCodeModal);
            enterCodeModal.removeClass(this.classes.modalOpen);
            enterCodeModal
                .find(this.elms.modalInput)
                .val("")
                .removeClass(this.classes.invalid);
        }
    }

    async validateStorePincode() {
        const pincode = $(this.elms.loginPageInput).val();

        const [res] = await bdjs.goAsync(
            bdapi.post("public/validate-team-pincode", {
                pincode: pincode,
            }),
        );

        return res.data;
    }

    async validatePlayerPincode() {
        const userId = this.userId;
        const pincode = $(this.elms.modalInput).val();

        const [res] = await bdjs.goAsync(
            bdapi.post("public/validate-player-pincode", {
                user_id: userId,
                pincode: pincode,
            }),
        );

        return res.data;
    }

    async getTeamPlayers() {
        const teamId = this.teamId;
        const [res] = await bdjs.goAsync(
            bdapi.post("public/get-team-members", {
                team_id: teamId,
            }),
        );
        if (!res || !res.data) {
            return false;
        }
        const response = res.data;
        if (response.success) {
            this.teamMembers = response.data;
            this.populatePlayerGrid(this.teamMembers);
        }
        return response.success;
    }

    populatePlayerGrid(teamMembers) {
        const playerGrid = $(this.elms.playerGrid);
        playerGrid.empty();
        teamMembers.forEach((member) => {
            const { ID, display_name, first_name, last_name, avatar, has_profile } = member;
            const fullName = `${first_name} ${last_name}`;

            const playerItem = $("<div class=\"player\"></div>")
                .attr("data-player-id", ID)
                .attr("data-has-profile", has_profile)
                .attr("data-first-name", first_name ?? "")
                .attr("data-last-name", last_name ?? "")
                .append(
                    $("<figure class=\"player__figure\"></figure>").append(
                        $("<img />", { class: "player__image", src: avatar, alt: fullName }),
                        $("<figcaption class=\"player__name\"></figcaption>").text(display_name),
                    ),
                );
            playerGrid.append(playerItem);
        });
    }

    async storePincodeAction(input) {
        const result = await this.validateStorePincode();
        if (!result.success) {
            // const errorMessage = result.data.message;
            input.val("").addClass(this.classes.invalid);
            this.updateLoginButtonState();
            return;
        } else {
            this.teamId = result.data.id;
            localStorage.setItem("teamId", String(result.data.id));
            this.onCloseCodeModal();
            await this.getTeamPlayers();
            $(this.elms.selectPlayerModal).addClass(
                this.classes.modalOpen,
            );
        }
    }

    async playerPincodeAction(input) {
        const result = await this.validatePlayerPincode();
        if (!result.success) {
            // const errorMessage = result.data.message;
            input.val("").addClass(this.classes.invalid);
            return;
        } else {
            this.userId = result.data.id;
            GameLogic.goToPage("home-page");
        }
    }
}

